import React, { useState, useEffect } from 'react';
import { Users, Target, CreditCard, AlertTriangle, TrendingUp, Clock, UserCheck, UserPlus, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Phone, CheckCircle, Plus, Pencil, Trash2, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useAdminAuth } from '../context/AdminAuthContext';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followUps, setFollowUps] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDateDetails, setShowDateDetails] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [taskTab, setTaskTab] = useState('today');
  const [adminUsers, setAdminUsers] = useState([]);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [completingTask, setCompletingTask] = useState(null);
  const [savingTask, setSavingTask] = useState(false);
  const emptyTaskForm = {
    activity_type: 'task', title: '', scheduled_at: '', notes: '',
    assigned_admin_id: '', assigned_admin_name: '', status: 'incomplete', reminder: 'none'
  };
  const [taskForm, setTaskForm] = useState(emptyTaskForm);
  const [completionForm, setCompletionForm] = useState({ completion_note: '', next_follow_up_at: '' });
  const { api, admin } = useAdminAuth();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, targetsRes] = await Promise.all([
        api.get('/api/admin/dashboard/stats'),
        api.get('/api/admin/target-members?limit=100')
      ]);
      setStats(statsRes.data);

      // Extract follow-ups from target members
      const targets = targetsRes.data.members || [];
      const allFollowUps = [];
      targets.forEach(target => {
        // Check for follow-ups in the follow_ups array that have next_follow_up dates
        if (target.follow_ups && target.follow_ups.length > 0) {
          target.follow_ups.forEach(fu => {
            // Only include follow-ups that have a next_follow_up date
            if (fu.next_follow_up) {
              allFollowUps.push({
                ...fu,
                company_name: target.company_name,
                contact_name: target.contact_name,
                target_id: target.target_id
              });
            }
          });
        }
        // Also check for top-level next_follow_up date (most recent scheduled follow-up)
        if (target.next_follow_up) {
          // Check if we already added this follow-up from the follow_ups array
          const alreadyAdded = allFollowUps.some(fu =>
            fu.target_id === target.target_id &&
            fu.next_follow_up === target.next_follow_up
          );
          if (!alreadyAdded) {
            allFollowUps.push({
              id: 'scheduled-' + target.target_id,
              note: 'Scheduled follow-up',
              next_follow_up: target.next_follow_up,
              company_name: target.company_name,
              contact_name: target.contact_name,
              target_id: target.target_id,
              isScheduled: true
            });
          }
        }
      });
      allFollowUps.sort((a, b) => {
        const da = a.next_follow_up ? new Date(a.next_follow_up).getTime() : 0;
        const db = b.next_follow_up ? new Date(b.next_follow_up).getTime() : 0;
        return da - db;
      });
      setFollowUps(allFollowUps);
      
      let allTasks = [];
      try {
        const tasksRes = await api.get('/api/admin/tasks?limit=500');
        allTasks = [...(tasksRes.data.tasks || [])];
      } catch (error) {
        console.warn('Tasks could not be loaded:', error.message);
      }
      setTasks(allTasks);

      try {
        const usersRes = await api.get('/api/admin/users/?type=admin&limit=500');
        setAdminUsers(usersRes.data.users || []);
      } catch (error) {
        setAdminUsers([]);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDateKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getTasksForDate = (date) => {
    const dateKey = formatDateKey(date);
    return tasks.filter(task => {
      const taskDate = new Date(task.scheduled_at || task.scheduled_date || task.due_date || task.created_at);
      return formatDateKey(taskDate) === dateKey;
    });
  };

  const getAllEventsForDate = (date) => {
    const dateKey = formatDateKey(date);
    const dayFollowUps = followUps.filter(fu => {
      // Only show follow-ups that have a next_follow_up date matching the selected date
      if (!fu.next_follow_up) return false;
      const fuDate = new Date(fu.next_follow_up);
      return formatDateKey(fuDate) === dateKey;
    });
    const dayTasks = getTasksForDate(date);
    return { followUps: dayFollowUps, tasks: dayTasks };
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setShowDateDetails(true);
  };

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getEventColor = (type) => {
    switch (type) {
      case 'followup': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'task': return 'bg-green-100 text-green-800 border-green-200';
      case 'event': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'reminder': return 'bg-amber-100 text-amber-800 border-amber-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const startOfDay = (value = new Date()) => new Date(value.getFullYear(), value.getMonth(), value.getDate());
  const targetFollowUpActivities = followUps.map((followUp, index) => ({
    ...followUp,
    task_id: followUp.id || `scheduled-${followUp.target_id}-${index}`,
    activity_type: 'follow_up',
    title: followUp.company_name ? `Follow up: ${followUp.company_name}` : 'Follow-up',
    notes: followUp.note,
    scheduled_at: followUp.next_follow_up,
    status: followUp.status || 'incomplete',
    source: followUp.isScheduled ? 'target_scheduled' : 'target_follow_up',
  }));
  const filteredTasks = [...tasks, ...targetFollowUpActivities].filter((task) => {
    const due = new Date(task.scheduled_at || task.scheduled_date || task.due_date);
    if (Number.isNaN(due.getTime())) return false;
    const today = startOfDay();
    const dueDay = startOfDay(due);
    const daysAway = Math.round((dueDay - today) / 86400000);
    if (taskTab === 'backlog') return daysAway < 0 && task.status !== 'completed';
    if (taskTab === 'today') return daysAway === 0;
    if (taskTab === 'week') return daysAway >= 1 && daysAway <= 7 && task.status !== 'completed';
    if (taskTab === 'month') return daysAway >= 1 && daysAway <= 30 && task.status !== 'completed';
    return daysAway >= 1 && task.status !== 'completed';
  });

  const openAddTask = () => {
    setEditingTask(null);
    setTaskForm({ ...emptyTaskForm, assigned_admin_id: admin?.admin_id || '', assigned_admin_name: admin?.full_name || '' });
    setShowTaskDialog(true);
  };

  const openEditTask = (task) => {
    setEditingTask(task);
    setTaskForm({
      activity_type: task.activity_type || 'task', title: task.title || '',
      scheduled_at: (task.scheduled_at || '').slice(0, 16), notes: task.notes || '',
      assigned_admin_id: task.assigned_admin_id || '', assigned_admin_name: task.assigned_admin_name || '',
      status: task.status || 'incomplete', reminder: task.reminder || 'none'
    });
    setShowTaskDialog(true);
  };

  const saveTask = async () => {
    if (!taskForm.title.trim() || !taskForm.scheduled_at) {
      toast.error('Title and next action date/time are required');
      return;
    }
    try {
      setSavingTask(true);
      const selectedAdmin = adminUsers.find((user) => (user.admin_id || user.user_id) === taskForm.assigned_admin_id);
      const payload = { ...taskForm, assigned_admin_name: selectedAdmin?.full_name || taskForm.assigned_admin_name || admin?.full_name || '' };
      if (editingTask) await api.put(`/api/admin/tasks/${editingTask.task_id}`, payload);
      else await api.post('/api/admin/tasks', payload);
      toast.success(editingTask ? 'Activity updated' : 'Activity added');
      setShowTaskDialog(false);
      await fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to save activity');
    } finally {
      setSavingTask(false);
    }
  };

  const completeTask = async () => {
    try {
      setSavingTask(true);
      const endpoint = completingTask.source === 'target_follow_up'
        ? `/api/admin/tasks/target-follow-ups/${completingTask.target_id}/${completingTask.task_id}/complete`
        : completingTask.source === 'target_scheduled'
          ? `/api/admin/tasks/target-scheduled/${completingTask.target_id}/complete`
          : `/api/admin/tasks/${completingTask.task_id}/complete`;
      await api.post(endpoint, {
        completion_note: completionForm.completion_note || null,
        next_follow_up_at: completionForm.next_follow_up_at || null,
      });
      toast.success(completionForm.next_follow_up_at ? 'Completed and follow-up added' : 'Activity completed');
      setShowCompleteDialog(false);
      await fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to complete activity');
    } finally {
      setSavingTask(false);
    }
  };

  const reopenTask = async (task) => {
    try {
      const endpoint = task.source === 'target_follow_up'
        ? `/api/admin/tasks/target-follow-ups/${task.target_id}/${task.task_id}/reopen`
        : `/api/admin/tasks/${task.task_id}/reopen`;
      await api.post(endpoint);
      toast.success('Activity moved back to incomplete');
      await fetchDashboardData();
    } catch (error) {
      toast.error('Unable to reopen activity');
    }
  };

  const removeTask = async (task) => {
    if (!window.confirm(`Remove “${task.title}”? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/admin/tasks/${task.task_id}`);
      toast.success('Activity removed');
      await fetchDashboardData();
    } catch (error) {
      toast.error('Unable to remove activity');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-gray-500">Welcome back, {admin?.full_name}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">New Registrations</p>
                <p className="text-3xl font-bold">{stats?.registrations?.pending || 0}</p>
                <p className="text-purple-200 text-xs mt-1">
                  {stats?.registrations?.today || 0} today
                </p>
              </div>
              <UserPlus size={40} className="text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Members</p>
                <p className="text-3xl font-bold">{stats?.members?.total || 0}</p>
                <p className="text-blue-200 text-xs mt-1">
                  {stats?.members?.active || 0} active
                </p>
              </div>
              <Users size={40} className="text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Target Prospects</p>
                <p className="text-3xl font-bold">{stats?.targets?.total || 0}</p>
                <p className="text-emerald-200 text-xs mt-1">
                  {stats?.targets?.new || 0} new
                </p>
              </div>
              <Target size={40} className="text-emerald-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Pending Payments</p>
                <p className="text-3xl font-bold">{stats?.payments?.pending || 0}</p>
                <p className="text-amber-200 text-xs mt-1">
                  {stats?.payments?.recent || 0} this month
                </p>
              </div>
              <CreditCard size={40} className="text-amber-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Active Restrictions</p>
                <p className="text-3xl font-bold">{stats?.restrictions?.active || 0}</p>
                <p className="text-red-200 text-xs mt-1">
                  Members restricted
                </p>
              </div>
              <AlertTriangle size={40} className="text-red-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <UserCheck size={24} className="text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.registrations?.reviewing || 0}</p>
                <p className="text-sm text-gray-500">Under Review</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <TrendingUp size={24} className="text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.registrations?.approved_today || 0}</p>
                <p className="text-sm text-gray-500">Approved Today</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Target size={24} className="text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.targets?.contacted || 0}</p>
                <p className="text-sm text-gray-500">Prospects Contacted</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Clock size={24} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{tasks.filter((task) => task.status !== 'completed').length}</p>
                <p className="text-sm text-gray-500">Open Tasks</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar and Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendar View */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center">
                <CalendarIcon size={20} className="mr-2 text-blue-600" />
                Activity Calendar
              </span>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)}>
                  <ChevronLeft size={16} />
                </Button>
                <span className="text-sm font-medium">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </span>
                <Button variant="outline" size="sm" onClick={() => navigateMonth(1)}>
                  <ChevronRight size={16} />
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Legend */}
            <div className="flex flex-wrap gap-3 mb-3 text-xs">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-100 rounded mr-1"></div>
                <span>Follow-ups</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-100 rounded mr-1"></div>
                <span>Tasks</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-purple-100 rounded mr-1"></div>
                <span>Events</span>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-gray-500 mb-2">
              {weekDays.map(day => (
                <div key={day} className="py-1">{day}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: getFirstDayOfMonth(currentDate) }).map((_, idx) => (
                <div key={`empty-${idx}`} className="h-20 bg-gray-50 rounded" />
              ))}
              {Array.from({ length: getDaysInMonth(currentDate) }).map((_, idx) => {
                const day = idx + 1;
                const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                const { followUps: dayFollowUps, tasks: dayTasks } = getAllEventsForDate(date);
                const isToday = formatDateKey(date) === formatDateKey(new Date());
                const totalEvents = dayFollowUps.length + dayTasks.length;

                return (
                  <div
                    key={day}
                    onClick={() => handleDateClick(date)}
                    className={`h-20 border rounded p-1 overflow-hidden cursor-pointer hover:bg-gray-50 transition-colors ${isToday ? 'bg-blue-50 border-blue-300' : 'bg-white'}`}
                  >
                    <div className={`text-xs font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
                      {day}
                    </div>
                    {/* Follow-ups - Blue */}
                    {dayFollowUps.slice(0, 1).map((fu, fuIdx) => (
                      <div
                        key={fuIdx}
                        className="text-[10px] bg-blue-100 text-blue-800 rounded px-1 py-0.5 mb-0.5 truncate"
                        title={`Follow-up: ${fu.company_name}`}
                      >
                        {fu.company_name}
                      </div>
                    ))}
                    {/* Tasks - Green */}
                    {dayTasks.slice(0, 1).map((task, taskIdx) => (
                      <div
                        key={taskIdx}
                        className="text-[10px] bg-green-100 text-green-800 rounded px-1 py-0.5 mb-0.5 truncate"
                        title={`Task: ${task.title}`}
                      >
                        {task.title}
                      </div>
                    ))}
                    {totalEvents > 2 && (
                      <div className="text-[10px] text-gray-500">+{totalEvents - 2} more</div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Combined Upcoming Events */}
            {(followUps.length > 0 || tasks.length > 0) && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="text-sm font-medium mb-2">Upcoming Activities</h4>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {/* Follow-ups */}
                  {followUps.slice(0, 3).map((fu, idx) => (
                    <div key={`fu-${idx}`} className="flex items-center justify-between text-sm p-2 bg-blue-50 rounded">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full" />
                        <span className="font-medium">{fu.company_name}</span>
                        <span className="text-gray-500 text-xs">- {fu.contact_name}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {fu.next_follow_up
                          ? new Date(fu.next_follow_up).toLocaleDateString()
                          : new Date(fu.added_at).toLocaleDateString()
                        }
                      </span>
                    </div>
                  ))}
                  {/* Tasks */}
                  {tasks.slice(0, 3).map((task, idx) => (
                    <div key={`task-${idx}`} className="flex items-center justify-between text-sm p-2 bg-green-50 rounded">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full" />
                        <span className="font-medium">{task.title}</span>
                        <span className="text-gray-500 text-xs">- {task.type || 'Task'}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {task.scheduled_at
                          ? new Date(task.scheduled_at).toLocaleDateString()
                          : new Date(task.created_at).toLocaleDateString()
                        }
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Date Details Dialog */}
        {selectedDate && (
          <Dialog open={showDateDetails} onOpenChange={setShowDateDetails}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>Activities for {selectedDate.toLocaleDateString()}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {(() => {
                  const { followUps: dayFollowUps, tasks: dayTasks } = getAllEventsForDate(selectedDate);
                  const totalEvents = dayFollowUps.length + dayTasks.length;

                  if (totalEvents === 0) {
                    return <p className="text-gray-500 text-center py-4">No activities for this date</p>;
                  }

                  return (
                    <>
                      {dayFollowUps.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-blue-600 mb-2 flex items-center">
                            <Phone size={14} className="mr-1" /> Follow-ups ({dayFollowUps.length})
                          </h4>
                          <div className="space-y-2">
                            {dayFollowUps.map((fu, idx) => (
                              <div key={idx} className="p-2 bg-blue-50 rounded text-sm">
                                <div className="flex items-center justify-between gap-2">
                                  <p className={`font-medium ${fu.status === 'completed' ? 'text-gray-500 line-through' : ''}`}>{fu.company_name}</p>
                                  <span className={`rounded-full px-2 py-0.5 text-[10px] ${fu.status === 'completed' ? 'bg-green-200 text-green-800' : 'bg-amber-100 text-amber-800'}`}>{fu.status || 'incomplete'}</span>
                                </div>
                                <p className="text-gray-600 text-xs">{fu.contact_name}</p>
                                {fu.note && <p className="text-gray-500 text-xs mt-1">{fu.note}</p>}
                                {fu.completion_note && <p className="mt-1 text-xs text-green-700">Completion note: {fu.completion_note}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {dayTasks.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-green-600 mb-2 flex items-center">
                            <CheckCircle size={14} className="mr-1" /> Tasks ({dayTasks.length})
                          </h4>
                          <div className="space-y-2">
                            {dayTasks.map((task, idx) => (
                              <div key={idx} className="p-2 bg-green-50 rounded text-sm">
                                <div className="flex items-center justify-between gap-2">
                                  <p className="font-medium">{task.title}</p>
                                  <span className={`rounded-full px-2 py-0.5 text-[10px] ${task.status === 'completed' ? 'bg-green-200 text-green-800' : 'bg-amber-100 text-amber-800'}`}>{task.status}</span>
                                </div>
                                {task.notes && <p className="text-gray-500 text-xs mt-1">{task.notes}</p>}
                                {task.completion_note && <p className="mt-1 text-xs text-green-700">Completion note: {task.completion_note}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Task Manager */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Tasks</span>
              <Button size="sm" onClick={openAddTask}><Plus size={16} className="mr-1" /> Add</Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex flex-wrap gap-2" role="tablist" aria-label="Task date range">
              {[
                ['backlog', 'Backlog'], ['today', 'Today'], ['week', 'Next 7 Days'],
                ['month', 'Next 30 Days'], ['all', 'All Future']
              ].map(([value, label]) => (
                <button key={value} type="button" role="tab" aria-selected={taskTab === value} onClick={() => setTaskTab(value)} className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${taskTab === value ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {label}
                </button>
              ))}
            </div>
            {filteredTasks.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No activities in this period</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredTasks.map((task) => (
                  <div key={task.task_id} className={`rounded-lg border p-3 ${task.status === 'completed' ? 'bg-green-50/60' : 'bg-gray-50'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className={`text-sm font-medium ${task.status === 'completed' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{task.title}</p>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] ${getEventColor(task.activity_type || 'task')}`}>{(task.activity_type || 'task').replace('_', ' ')}</span>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          {new Date(task.scheduled_at).toLocaleString()} {task.assigned_admin_name ? `• ${task.assigned_admin_name}` : ''}
                        </p>
                        {task.notes && <p className="mt-1 truncate text-xs text-gray-600">{task.notes}</p>}
                        {task.completion_note && <p className="mt-1 text-xs text-green-700">Completed: {task.completion_note}</p>}
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {task.status === 'completed' ? (
                          <Button variant="ghost" size="sm" title="Reopen" onClick={() => reopenTask(task)}><RotateCcw size={15} /></Button>
                        ) : (
                          <Button variant="ghost" size="sm" title="Mark complete" onClick={() => { setCompletingTask(task); setCompletionForm({ completion_note: '', next_follow_up_at: '' }); setShowCompleteDialog(true); }}><CheckCircle size={16} className="text-green-600" /></Button>
                        )}
                        {!task.source && <Button variant="ghost" size="sm" title="Edit" onClick={() => openEditTask(task)}><Pencil size={15} /></Button>}
                        {!task.source && <Button variant="ghost" size="sm" title="Remove" onClick={() => removeTask(task)}><Trash2 size={15} className="text-red-500" /></Button>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingTask ? 'Edit Activity' : 'Add Activity'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Activity type</Label><select className="mt-1 w-full rounded-md border px-3 py-2" value={taskForm.activity_type} onChange={(e) => setTaskForm({ ...taskForm, activity_type: e.target.value })}><option value="task">Task</option><option value="event">Event</option><option value="follow_up">Follow-up</option></select></div>
            <div><Label>Status</Label><select className="mt-1 w-full rounded-md border px-3 py-2" value={taskForm.status} onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })}><option value="incomplete">Incomplete</option><option value="completed">Completed</option></select></div>
            <div className="col-span-2"><Label>Title</Label><Input className="mt-1" value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} placeholder="Enter task or event title" /></div>
            <div className="col-span-2"><Label>Next action date and time</Label><Input className="mt-1" type="datetime-local" value={taskForm.scheduled_at} onChange={(e) => setTaskForm({ ...taskForm, scheduled_at: e.target.value })} /></div>
            <div><Label>Assigned admin</Label><select className="mt-1 w-full rounded-md border px-3 py-2" value={taskForm.assigned_admin_id} onChange={(e) => setTaskForm({ ...taskForm, assigned_admin_id: e.target.value })}><option value="">Unassigned</option>{adminUsers.map((user) => <option key={user.admin_id || user.user_id} value={user.admin_id || user.user_id}>{user.full_name || user.email}</option>)}</select></div>
            <div><Label>Reminder</Label><select className="mt-1 w-full rounded-md border px-3 py-2" value={taskForm.reminder} onChange={(e) => setTaskForm({ ...taskForm, reminder: e.target.value })}><option value="none">None</option><option value="at_time">At activity time</option><option value="15_minutes">15 minutes before</option><option value="1_hour">1 hour before</option><option value="1_day">1 day before</option></select></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea className="mt-1" rows={4} value={taskForm.notes} onChange={(e) => setTaskForm({ ...taskForm, notes: e.target.value })} placeholder="Add context or next steps" /></div>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button><Button onClick={saveTask} disabled={savingTask}>{savingTask ? 'Saving…' : 'Save Activity'}</Button></div>
        </DialogContent>
      </Dialog>

      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Complete Activity</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">Mark “{completingTask?.title}” as completed. Both fields below are optional.</p>
          <div><Label>Completion note</Label><Textarea className="mt-1" rows={3} value={completionForm.completion_note} onChange={(e) => setCompletionForm({ ...completionForm, completion_note: e.target.value })} placeholder="Outcome or completion details" /></div>
          <div><Label>Next follow-up date and time</Label><Input className="mt-1" type="datetime-local" value={completionForm.next_follow_up_at} onChange={(e) => setCompletionForm({ ...completionForm, next_follow_up_at: e.target.value })} /></div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowCompleteDialog(false)}>Cancel</Button><Button onClick={completeTask} disabled={savingTask}>{savingTask ? 'Saving…' : 'Mark Complete'}</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
